import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { en } from '@/i18n/en';
import { ro } from '@/i18n/ro';

type Language = 'en' | 'ro';
type Translations = typeof en;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const dictionaries = { en, ro };

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('add-lang');
    if (saved === 'ro' || saved === 'en') return saved;
    const browserLang = navigator.language || '';
    return browserLang.startsWith('ro') ? 'ro' : 'en';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('add-lang', lang);
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = dictionaries[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const fallback: LanguageContextType = {
  language: 'en',
  setLanguage: () => {},
  t: en,
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  return context ?? fallback;
};
